import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldAlert, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const DeleteAccountModal = ({ isOpen, onClose, onSubmit, isSubmitting, error, requirePassword = true }) => {
  const { t, i18n } = useTranslation('settings');
  const confirmationWord = t('deleteAccountModal.confirmationWord');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [validationError, setValidationError] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setPassword('');
      setConfirmation('');
      setValidationError('');
    }
  }, [isOpen]);

  const handleSubmit = (event) => {
    event.preventDefault();
    setValidationError('');

    if (requirePassword && !password) {
      setValidationError(t('deleteAccountModal.passwordRequired'));
      return;
    }

    const normalizedConfirmation = confirmation.trim().toLocaleUpperCase(i18n.language);

    if (normalizedConfirmation !== confirmationWord) {
      setValidationError(t('deleteAccountModal.confirmationRequired', { word: confirmationWord }));
      return;
    }

    onSubmit({ password: requirePassword ? password : null });
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/70 backdrop-blur-md px-4"
        onClick={onClose}
      >
        <motion.form
          onClick={(event) => event.stopPropagation()}
          onSubmit={handleSubmit}
          initial={{ opacity: 0, scale: 0.93, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.93, y: 30 }}
          transition={{ type: 'spring', duration: 0.35 }}
          className="w-full max-w-xl bg-gradient-to-br from-dark-900 via-dark-900/95 to-dark-950 border border-red-900/50 rounded-2xl shadow-[0_30px_80px_-20px_rgba(248,113,113,0.45)] overflow-hidden"
        >
          <div className="flex items-center justify-between px-6 py-6 border-b border-red-900/40">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-xl bg-red-500/20 text-red-400 flex items-center justify-center border border-red-500/30">
                <ShieldAlert className="w-6 h-6" strokeWidth={2.4} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">{t('deleteAccountModal.title')}</h3>
                <p className="text-sm text-red-200/80">
                  {t('deleteAccountModal.subtitle')}
                </p>
              </div>
            </div>
            <motion.button
              type="button"
              onClick={onClose}
              whileHover={{ scale: 1.08 }}
              whileTap={{ scale: 0.94 }}
              className="p-2 rounded-xl text-red-300 hover:text-white hover:bg-red-500/20 transition-all"
              aria-label={t('deleteAccountModal.close')}
            >
              <X className="w-5 h-5" />
            </motion.button>
          </div>

          <div className="px-6 py-6 space-y-5">
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 text-sm text-red-200 leading-relaxed">
              <p className="font-semibold mb-1">{t('deleteAccountModal.dataToBeDeletedTitle')}</p>
              <ul className="list-disc list-inside space-y-1 text-red-100/90">
                <li>{t('deleteAccountModal.dataItems.notes')}</li>
                <li>{t('deleteAccountModal.dataItems.calendars')}</li>
                <li>{t('deleteAccountModal.dataItems.breaks')}</li>
                <li>{t('deleteAccountModal.dataItems.profile')}</li>
              </ul>
            </div>

            {requirePassword ? (
              <div className="space-y-2">
                <label className="text-sm font-medium text-red-200">
                  {t('deleteAccountModal.passwordLabel')}
                </label>
                <input
                  type="password"
                  className="w-full px-4 py-3 rounded-xl border border-red-500/40 bg-dark-900/60 text-white placeholder-red-200/70 focus:outline-none focus:ring-2 focus:ring-red-400/70 focus:border-red-400 transition"
                  placeholder="••••••••"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
              </div>
            ) : (
              <p className="text-sm text-red-200/90 bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3">
                {t('deleteAccountModal.oauthNoPasswordHint')}
              </p>
            )}

            <div className="space-y-2">
              <label className="text-sm font-medium text-red-200">
                {t('deleteAccountModal.confirmationLabelPrefix')} <span className="font-semibold">{confirmationWord}</span> {t('deleteAccountModal.confirmationLabelSuffix')}
              </label>
              <input
                type="text"
                className="w-full px-4 py-3 rounded-xl border border-red-500/30 bg-dark-900/60 text-white uppercase placeholder-red-200/70 focus:outline-none focus:ring-2 focus:ring-red-400/70 focus:border-red-400 tracking-widest transition"
                placeholder={confirmationWord}
                value={confirmation}
                onChange={(event) => setConfirmation(event.target.value)}
              />
            </div>

            {(validationError || error) && (
              <p className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3">
                {validationError || error}
              </p>
            )}
          </div>

          <div className="px-6 py-5 border-t border-red-900/40 bg-dark-900/70 flex items-center justify-end space-x-3">
            <motion.button
              type="button"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={onClose}
              className="px-5 py-2.5 rounded-xl font-medium text-red-200/90 hover:text-white hover:bg-red-500/10 border border-red-500/30 transition-all"
            >
              {t('deleteAccountModal.cancel')}
            </motion.button>
            <motion.button
              type="submit"
              whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
              whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
              disabled={isSubmitting}
              className="px-6 py-2.5 rounded-xl font-semibold text-white bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 transition-all shadow-lg shadow-red-600/30 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isSubmitting ? t('deleteAccountModal.deleting') : t('deleteAccountModal.deleteAccount')}
            </motion.button>
          </div>
        </motion.form>
      </motion.div>
    </AnimatePresence>
  );
};

export default DeleteAccountModal;


